import { format } from "date-fns";
import { it } from 'date-fns/locale/it';
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAppDispatch, useAppSelector } from '../../../lib/redux/hooks';
import { LogoSpinnerComponent } from "../../../lib/spinner/spinnerLogo";
import { CustomAccordion } from "../../../ui/molecules/accordion";
import { Calendar } from "../../../ui/molecules/calendar";
import { MadfarmInput } from "../../../ui/molecules/input/newInput";
import { CustomRadio } from "../../../ui/molecules/radioButton";
import { MadfarmStaticSelect } from "../../../ui/molecules/select/newSelect/static";
import TextArea from "../../../ui/molecules/textArea/TextArea";
import { Province, Region, formaGiuridica, regions } from "../../../utils";
import { capComuneMap } from "../../../utils/capComuniMap"; // Aggiunto import
import { provinceComuniMap } from "../../../utils/listaComuni";
import { GetAllOdc, resetGetAllOdcStatus } from "../../odc/slice";
import { setCustomerUpdateRequestField } from "../feature/slice";
import { setEndingDate, setEntryOdc } from "../slice";
import '../style.scss';

registerLocale('it', it)

type OptionType = {
    value: string;
    label: string;
};

export function BoxAnagraficaEdit() {
    const customerState = useAppSelector(state => state.customer)
    const odcState = useAppSelector(state => state.odc)

    const dispatch = useAppDispatch()
    const [expanded, setExpanded] = React.useState<string | false>("panel1");

    const handleToggleAccordion = (panel: string) => {
        setExpanded(expanded === panel ? false : panel);
    };

    const [dateActivate, setDataActivate] = useState<Date | undefined>(undefined)
    const [dateDeActivate, setDataDeActivate] = useState<Date | undefined>(undefined)

    const [provinceOptions, setProvinceOptions] = useState<OptionType[]>([]);
    const [rpProvinceOptions, setRpProvinceOptions] = useState<OptionType[]>([]);
    const [comuneOptions, setComuneOptions] = useState<OptionType[]>([]);
    const [rpComuneOptions, setRpComuneOptions] = useState<OptionType[]>([]);
    const [odc, setOdc] = useState<OptionType[]>([])

    const regionOptions: OptionType[] = regions.map((region: Region) => ({
        value: region.name,
        label: region.name
    }));

    useEffect(() => {
        dispatch(GetAllOdc({ itemsPerPage: 0, page: 0 }))
    }, [dispatch])

    useEffect(() => {
        if (odcState.getAllOdcStatus === 'successfully' && odcState.getAllOdcResponse !== undefined) {
            dispatch(resetGetAllOdcStatus())
            const data = odcState.getAllOdcResponse.data.map(odc => ({
                value: odc.id,
                label: odc.companyName
            } as OptionType))
            setOdc(data)
        }
    }, [odcState.getAllOdcStatus, odcState.getAllOdcResponse, dispatch])

    useEffect(() => {
        if (customerState.getCustomerByIdStatus === 'successfully') {
            const data = customerState.getCustomerByIdResponse;

            if (data?.countryRegion) {
                const foundRegion = regions.find((region: Region) => region.name === data.countryRegion.toString());
                if (foundRegion) {
                    setProvinceOptions(foundRegion.provinces.map((province: Province) => ({
                        value: province.code, label: province.name
                    })));
                } else { setProvinceOptions([]); }
            } else { setProvinceOptions([]); }

            if (data?.province) {
                const comuni = provinceComuniMap.get(data.province.toString()) || [];
                setComuneOptions(comuni.map((comune: string) => ({ value: comune, label: comune })));
            } else { setComuneOptions([]); }

            if (data?.rpCountryRegion) {
                const foundRegion = regions.find((region: Region) => region.name === data.rpCountryRegion.toString());
                if (foundRegion) {
                    setRpProvinceOptions(foundRegion.provinces.map((province: Province) => ({
                        value: province.code, label: province.name
                    })));
                } else { setRpProvinceOptions([]); }
            } else { setRpProvinceOptions([]); }

            if (data?.rpProvince) {
                const comuni = provinceComuniMap.get(data.rpProvince.toString()) || [];
                setRpComuneOptions(comuni.map((comune: string) => ({ value: comune, label: comune })));
            } else { setRpComuneOptions([]); }
        }
    }, [customerState.getCustomerByIdStatus, customerState.getCustomerByIdResponse]);

    const handleRegionChange = (selectedOption: OptionType | null) => {
        const value = selectedOption ? selectedOption.toString() : null;
        dispatch(setCustomerUpdateRequestField({ field: 'countryRegion', value }));
        dispatch(setCustomerUpdateRequestField({ field: 'province', value: null }));
        dispatch(setCustomerUpdateRequestField({ field: 'city', value: null }));

        if (value) {
            const foundRegion = regions.find(r => r.name === value);
            setProvinceOptions(foundRegion ? foundRegion.provinces.map(p => ({ value: p.code, label: p.name })) : []);
        } else {
            setProvinceOptions([]);
        }
        setComuneOptions([]);
    };

    const handleProvinceChange = (selectedOption: OptionType | null) => {
        const value = selectedOption ? selectedOption.toString() : null;
        dispatch(setCustomerUpdateRequestField({ field: 'province', value }));
        dispatch(setCustomerUpdateRequestField({ field: 'city', value: null }));

        if (value) {
            const comuni = provinceComuniMap.get(value) || [];
            setComuneOptions(comuni.map(c => ({ value: c, label: c })));
        } else {
            setComuneOptions([]);
        }
    };

    const handleRpRegionChange = (selectedOption: OptionType | null) => {
        const value = selectedOption ? selectedOption.toString() : null;
        dispatch(setCustomerUpdateRequestField({ field: 'rpCountryRegion', value }));
        dispatch(setCustomerUpdateRequestField({ field: 'rpProvince', value: null }));
        dispatch(setCustomerUpdateRequestField({ field: 'rpCity', value: null }));

        if (value) {
            const foundRegion = regions.find(r => r.name === value);
            setRpProvinceOptions(foundRegion ? foundRegion.provinces.map(p => ({ value: p.code, label: p.name })) : []);
        } else {
            setRpProvinceOptions([]);
        }
        setRpComuneOptions([]);
    };

    const handleRpProvinceChange = (selectedOption: OptionType | null) => {
        const value = selectedOption ? selectedOption.toString() : null;
        dispatch(setCustomerUpdateRequestField({ field: 'rpProvince', value }));
        dispatch(setCustomerUpdateRequestField({ field: 'rpCity', value: null }));

        if (value) {
            const comuni = provinceComuniMap.get(value) || [];
            setRpComuneOptions(comuni.map(c => ({ value: c, label: c })));
        } else {
            setRpComuneOptions([]);
        }
    };

    const applyCapAutocomplete = (cap: string, target: 'sede' | 'rp') => {
        if (cap.length !== 5) return;

        const results = capComuneMap.get(cap);
        if (!results || results.length === 0) return;

        const first = results[0];
        const foundRegion = regions.find((r: Region) => r.name === first.region);

        const newProvinceOptions = foundRegion
            ? foundRegion.provinces.map((p: Province) => ({ value: p.code, label: p.name }))
            : [];

        const allComuniForProvince = provinceComuniMap.get(first.province) || [];
        const newComuneOptionsFull = allComuniForProvince.map((c: string) => ({ value: c, label: c }));
        const filteredComuneOptions = results.map(r => ({ value: r.comune, label: r.comune }));

        const finalComuneOptions = results.length === 1 ? newComuneOptionsFull : filteredComuneOptions;
        const finalCityValue = results.length === 1 ? first.comune : null;

        if (target === 'sede') {
            setProvinceOptions(newProvinceOptions);
            setComuneOptions(finalComuneOptions);
            setRpProvinceOptions(newProvinceOptions);
            setRpComuneOptions(finalComuneOptions);

            dispatch(setCustomerUpdateRequestField({ field: 'countryRegion', value: first.region }));
            dispatch(setCustomerUpdateRequestField({ field: 'province', value: first.province }));
            dispatch(setCustomerUpdateRequestField({ field: 'city', value: finalCityValue }));

            dispatch(setCustomerUpdateRequestField({ field: 'rpCountryRegion', value: first.region }));
            dispatch(setCustomerUpdateRequestField({ field: 'rpProvince', value: first.province }));
            dispatch(setCustomerUpdateRequestField({ field: 'rpCity', value: finalCityValue }));
        } else {
            setRpProvinceOptions(newProvinceOptions);
            setRpComuneOptions(finalComuneOptions);

            dispatch(setCustomerUpdateRequestField({ field: 'rpCountryRegion', value: first.region }));
            dispatch(setCustomerUpdateRequestField({ field: 'rpProvince', value: first.province }));
            dispatch(setCustomerUpdateRequestField({ field: 'rpCity', value: finalCityValue }));
        }
    };


    if (customerState.getCustomerByIdStatus === 'loading') {
        return (
            <LogoSpinnerComponent fullScreen />
        )
    }

    return (
        <div className="p-6 flex flex-col gap-6">
            <CustomAccordion
                id="panel1"
                title={<span className="text-uppercase text-white label-input">Anagrafica</span>}
                expanded={expanded === 'panel1'}
                onChange={() => handleToggleAccordion('panel1')}
                detailsClassName="flex flex-col gap-4"
            >
                <div className='grid grid-cols gap-4'>
                    <MadfarmInput
                        required
                        label={'Ragione Sociale'}
                        placeholder="Ragione Sociale"
                        value={customerState.customerUpdateRequest.companyName || ''}
                        onChangeText={(text) => dispatch(setCustomerUpdateRequestField({ field: 'companyName', value: text }))}
                    />
                </div>
                <div className='grid grid-cols-3 gap-4'>
                    <MadfarmInput
                        required
                        label={'CUAA'}
                        placeholder="CUAA"
                        value={customerState.customerUpdateRequest.cuaa || ''}
                        onChangeText={(text) => dispatch(setCustomerUpdateRequestField({ field: 'cuaa', value: text }))}
                    />
                    <MadfarmInput
                        required
                        label={'Partita IVA'}
                        placeholder="Partita IVA"
                        value={customerState.customerUpdateRequest.vat || ''}
                        onChangeText={(text) => dispatch(setCustomerUpdateRequestField({ field: 'vat', value: text }))}
                    />
                    <MadfarmStaticSelect
                        required
                        label='Forma Giuridica'
                        noOptionsMessage='Nessuna forma giuridica disponibile'
                        errorLabel="Scegliere Forma Giuridica"
                        placeholder={'Forma Giuridica'}
                        value={customerState.customerUpdateRequest.legalForm}
                        options={formaGiuridica}
                        onChange={(value) => {
                            //@ts-ignore
                            const newValue = value !== null ? value.value : null;
                            dispatch(setCustomerUpdateRequestField({ field: 'legalForm', value: newValue }))
                        }}
                    />
                </div>
            </CustomAccordion>
            <CustomAccordion
                id="panel2"
                title={<span className="text-uppercase text-white label-input">Sede Legale</span>}
                expanded={expanded === 'panel2'}
                onChange={() => handleToggleAccordion('panel2')}
                detailsClassName="flex flex-col gap-4"
            >
                <div className='grid grid-cols gap-4'>
                    <MadfarmInput
                        required
                        label={'Indirizzo'}
                        placeholder="Indirizzo"
                        value={customerState.customerUpdateRequest.address || ''}
                        onChangeText={(text) => {
                            dispatch(setCustomerUpdateRequestField({ field: 'address', value: text }))
                            dispatch(setCustomerUpdateRequestField({ field: 'rpAddress', value: text }))
                        }}
                    />
                </div>
                <div className='grid grid-cols-2 gap-4'>
                    <MadfarmInput
                        required
                        label={'CAP'}
                        placeholder="CAP"
                        value={customerState.customerUpdateRequest.zipCode || ''}
                        onChangeText={(text) => {
                            dispatch(setCustomerUpdateRequestField({ field: 'zipCode', value: text }));
                            dispatch(setCustomerUpdateRequestField({ field: 'rpZipCode', value: text }));
                            applyCapAutocomplete(text, 'sede');
                        }}
                    />
                    <MadfarmStaticSelect
                        required
                        label="Comune"
                        noOptionsMessage="Nessun comune trovata"
                        placeholder={'Comune'}
                        options={comuneOptions}
                        value={customerState.customerUpdateRequest.city}
                        onChange={(value) => {
                            //@ts-ignore
                            const newValue = value !== null ? value.value : null;
                            dispatch(setCustomerUpdateRequestField({ field: 'city', value: newValue }))
                            dispatch(setCustomerUpdateRequestField({ field: 'rpCity', value: newValue }))
                        }}
                    />
                </div>
                <div className='grid grid-cols-2 gap-4'>
                    <MadfarmStaticSelect
                        required
                        label="Provincia"
                        noOptionsMessage="Nessuna provincia trovata"
                        placeholder={'Provincia'}
                        value={customerState.customerUpdateRequest.province}
                        options={provinceOptions}
                        onChange={(val) => handleProvinceChange(val as unknown as OptionType | null)}
                    />
                    <MadfarmStaticSelect
                        required
                        label="Regione"
                        noOptionsMessage="Nessuna regione trovata"
                        value={customerState.customerUpdateRequest.countryRegion}
                        placeholder={'Regione'}
                        options={regionOptions}
                        onChange={(val) => handleRegionChange(val as unknown as OptionType | null)}
                    />
                </div>
            </CustomAccordion>
            <CustomAccordion
                id="panel3"
                title={<span className="text-uppercase text-white label-input">Recapito Postale</span>}
                expanded={expanded === 'panel3'}
                onChange={() => handleToggleAccordion('panel3')}
                detailsClassName="flex flex-col gap-4"
            >
                <div className='grid grid-cols gap-4'>
                    <MadfarmInput
                        required
                        label={'Indirizzo'}
                        placeholder="Indirizzo"
                        onChangeText={(text) => dispatch(setCustomerUpdateRequestField({ field: 'rpAddress', value: text }))}
                        value={customerState.customerUpdateRequest.rpAddress || ''}
                    />
                </div>
                <div className='grid grid-cols-2 gap-4'>
                    <MadfarmInput
                        required
                        label={'CAP'}
                        placeholder="CAP"
                        value={customerState.customerUpdateRequest.rpZipCode || ''}
                        onChangeText={(text) => {
                            dispatch(setCustomerUpdateRequestField({ field: 'rpZipCode', value: text }));
                            applyCapAutocomplete(text, 'rp');
                        }}
                    />
                    <MadfarmStaticSelect
                        required
                        label="Comune"
                        noOptionsMessage="Nessun comune trovata"
                        placeholder={'Comune'}
                        options={rpComuneOptions}
                        value={customerState.customerUpdateRequest.rpCity}
                        onChange={(value) => {
                            //@ts-ignore
                            const newValue = value !== null ? value.value : null;
                            dispatch(setCustomerUpdateRequestField({ field: 'rpCity', value: newValue }))
                        }}
                    />
                </div>
                <div className='grid grid-cols-2 gap-4'>
                    <MadfarmStaticSelect
                        required
                        label="Provincia"
                        noOptionsMessage="Nessuna provincia trovata"
                        placeholder={'Provincia'}
                        value={customerState.customerUpdateRequest.rpProvince}
                        options={rpProvinceOptions}
                        onChange={(val) => handleRpProvinceChange(val as unknown as OptionType | null)}
                    />
                    <MadfarmStaticSelect
                        required
                        label="Regione"
                        noOptionsMessage="Nessuna regione trovata"
                        value={customerState.customerUpdateRequest.rpCountryRegion}
                        placeholder={'Regione'}
                        options={regionOptions}
                        onChange={(val) => handleRpRegionChange(val as unknown as OptionType | null)}
                    />
                </div>
            </CustomAccordion>
            <CustomAccordion
                id="panel4"
                title={<span className="text-uppercase text-white label-input">Dati Contabili</span>}
                expanded={expanded === 'panel4'}
                onChange={() => handleToggleAccordion('panel4')}
                detailsClassName="flex flex-col gap-4"
            >
                <div className='grid grid-cols-2 gap-4'>
                    <MadfarmInput
                        label={'Codice Destinatario'}
                        placeholder="Codice Destinatario"
                        value={customerState.customerUpdateRequest.addresseeCode ?? ""}
                        onChangeText={(text) => dispatch(setCustomerUpdateRequestField({ field: 'addresseeCode', value: text }))}
                    />
                    <MadfarmInput
                        label={'Codice Contabile / Partitario'}
                        placeholder="Codice Contabile / Partitario"
                        value={customerState.customerUpdateRequest.accountingCode ?? ""}
                        onChangeText={(text) => dispatch(setCustomerUpdateRequestField({ field: 'accountingCode', value: text }))}
                    />
                </div>
                <TextArea
                    label="Coordinate Bancarie"
                    value={customerState.customerUpdateRequest.bankDetails || ''}
                    onChangeText={(text) => dispatch(setCustomerUpdateRequestField({ field: 'bankDetails', value: text }))}
                />
            </CustomAccordion>
            <CustomAccordion
                id="panel5"
                title={<span className="text-uppercase text-white label-input">Ingresso nel sistema di controllo</span>}
                expanded={expanded === 'panel5'}
                onChange={() => handleToggleAccordion('panel5')}
                detailsClassName="flex flex-col gap-4"
            >
                <div className='grid grid-cols-2 gap-4'>
                    <Calendar
                        label="Data Ingresso"
                        selected={customerState.customerUpdateRequest.startingDate}
                        onChange={(date) => {
                            dispatch(setCustomerUpdateRequestField({ field: 'startingDate', value: format(new Date(date), 'yyyy-MM-dd') }))
                            setDataActivate(date)
                        }}
                    />
                </div>
                <div className="grid grid-cols-3 gap-6">
                    <div className="flex flex-col gap-4">
                        <div className="flex w-full justify-start items-center">
                            <CustomRadio
                                options={[{ label: 'Prima Notifica', value: 'PRIMA_NOTIFICA' }]}
                                selectedValue={customerState.customerUpdateRequest.entryReason}
                                onChange={(value) => dispatch(setCustomerUpdateRequestField({ field: 'entryReason', value: value }))}
                                id={'prima_notifica'}
                                name={'entry_group'}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col gap-4">
                        <CustomRadio
                            options={[{ label: 'Cambio Odc', value: 'CAMBIO_ODC_INGRESSO' }]}
                            selectedValue={customerState.customerUpdateRequest.entryReason}
                            onChange={(value) => dispatch(setCustomerUpdateRequestField({ field: 'entryReason', value: value }))}
                            id={'CHANGE_ODC_ENTRY'}
                            name={'entry_group'}
                        />
                        {customerState.customerUpdateRequest.entryReason === 'CAMBIO_ODC_INGRESSO' && (
                            <MadfarmStaticSelect
                                placeholder="ODC"
                                noOptionsMessage="Nessun ODC trovato"
                                options={odc}
                                value={customerState.customerUpdateRequest.entryOdcId}
                                onChange={(value) => {
                                    // @ts-ignore
                                    const newValue = value !== null ? value.value : null
                                    dispatch(setEntryOdc(newValue))
                                }}
                            />
                        )}
                    </div>
                    <div className="flex flex-col gap-4">
                        <CustomRadio
                            options={[{ label: 'Subentro', value: 'SOSTITUZIONE' }]}
                            selectedValue={customerState.customerUpdateRequest.entryReason}
                            onChange={(value) => dispatch(setCustomerUpdateRequestField({ field: 'entryReason', value: value }))}
                            id={'TAKEOVER'}
                            name={'entry_group'}
                        />
                        {customerState.customerUpdateRequest.entryReason === 'SOSTITUZIONE' && (
                            <MadfarmInput
                                placeholder="Subentro"
                                value={customerState.customerUpdateRequest.entryOdcId || ''}
                                onChangeText={(text) => dispatch(setCustomerUpdateRequestField({ field: 'entryOdcId', value: text }))}
                            />
                        )}
                    </div>
                </div>
            </CustomAccordion>
            <CustomAccordion
                id="panel6"
                title={<span className="text-uppercase text-white label-input">Uscita nel sistema di controllo</span>}
                expanded={expanded === 'panel6'}
                onChange={() => handleToggleAccordion('panel6')}
                detailsClassName="flex flex-col gap-4"
            >
                <div className='grid grid-cols-2 gap-4'>
                    <Calendar
                        label="Data Uscita"
                        selected={customerState.customerUpdateRequest.endingDate}
                        onChange={(date) => {
                            dispatch(setEndingDate(format(new Date(date), 'yyyy-MM-dd')))
                            setDataDeActivate(date)
                        }}
                    />
                </div>
                <div className="grid grid-cols-4 gap-6">
                    <div className="flex flex-col gap-4">
                        <div className="flex gap-4 w-full justify-start items-center">
                            <CustomRadio
                                options={[{ label: 'Recesso', value: 'RECESSO' }]}
                                selectedValue={customerState.customerUpdateRequest.exitReason}
                                onChange={(value) => dispatch(setCustomerUpdateRequestField({ field: 'exitReason', value: 'RECESSO' }))}
                                id={'recesso'}
                                name={'exit_group'}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col gap-4">
                        <div className="flex gap-4 w-full justify-start items-center">
                            <CustomRadio
                                options={[{ label: 'Esclusione', value: 'ESCLUSIONE' }]}
                                selectedValue={customerState.customerUpdateRequest.exitReason}
                                onChange={(value) => dispatch(setCustomerUpdateRequestField({ field: 'exitReason', value: 'ESCLUSIONE' }))}
                                id={'exlusion'}
                                name={'exit_group'}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col gap-4">
                        <CustomRadio
                            options={[{ label: 'Cambio Odc', value: 'CAMBIO_ODC_USCITA' }]}
                            selectedValue={customerState.customerUpdateRequest.exitReason}
                            onChange={(value) => dispatch(setCustomerUpdateRequestField({ field: 'exitReason', value: 'CAMBIO_ODC_USCITA' }))}
                            id={'CHANGE_ODC_EXIT'}
                            name={'exit_group'}
                        />
                        {customerState.customerUpdateRequest.exitReason === 'CAMBIO_ODC_USCITA' && (
                            <MadfarmStaticSelect
                                placeholder="ODC"
                                noOptionsMessage="Nessun ODC trovato"
                                value={customerState.customerUpdateRequest.exitOdcId}
                                options={odc}
                                onChange={(value) => {
                                    // @ts-ignore
                                    const newValue = value !== null ? value.value : null
                                    dispatch(setCustomerUpdateRequestField({ field: 'exitOdcId', value: newValue }))
                                }}
                            />
                        )}
                    </div>
                    <div className="flex flex-col gap-4">
                        <CustomRadio
                            options={[{ label: 'Passaggio ad altro operatore', value: 'CAMBIO_OPERATORE' }]}
                            selectedValue={customerState.customerUpdateRequest.exitReason}
                            onChange={(value) => dispatch(setCustomerUpdateRequestField({ field: 'exitReason', value: 'CAMBIO_OPERATORE' }))}
                            id={'passaggio_altro_operatore'}
                            name={'exit_group'}
                        />
                    </div>
                </div>
            </CustomAccordion>
        </div>
    )
}